import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions
} from '@tanstack/react-query';
import { RegistryItemsAPI } from '@newo/api/gateway';
import type { TRegistryItem } from '@newo/api/gateway';
import { designerV2API } from '@/services/designer/designerV2Client';
import axios from '@/utils/api/axiosInstance';

const registryItemsAPI = new RegistryItemsAPI(axios);

export const useRegistryItemsQuery = (
    registryId?: string,
    options?: Omit<UseQueryOptions<TRegistryItem[]>, 'queryKey' | 'queryFn'>
) => {
    return useQuery({
        queryKey: ['registry/items', registryId],
        queryFn: async () => {
            if (!registryId) {
                throw new Error('Registry Id is required');
            }

            return registryItemsAPI.getRegistryItems(registryId);
        },
        ...options,
        enabled: !!registryId
    });
};

export const useDeleteRegistryItemMutation = () => {
    return useMutation({
        mutationFn: async ({
            registryItemId,
            registryId
        }: {
            registryItemId: string;
            registryId: string;
        }) => registryItemsAPI.deleteRegistryItem(registryId, registryItemId)
    });
};

export const usePublishRegistryItemMutation = (
    registryId: string,
    isProjectsV2Enabled = false
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (registryItemId: string) => {
            if (isProjectsV2Enabled) {
                return designerV2API.publishModule(registryId, registryItemId);
            }

            return registryItemsAPI.publishRegistryItem(
                registryId,
                registryItemId
            );
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ['registry/items/grouped']
                }),
                queryClient.invalidateQueries({
                    queryKey: ['registry/items/versions']
                })
            ]);
        }
    });
};
